import * as React from 'react';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { displayNameFacet }
  from '../../../facets/ItemFacets';
import { INameFieldProps, NameField } from '../name/NameField';
import { getNewSignal, getStorageSignal, getLifeCycleSignals, ISignalData } from '../signal/SignalIcon';
import { DotDotDotField, IDotDotDotFieldProps } from '../dotdotdot/DotDotDotField';
import { ShareHeroField } from '../shareHero/ShareHeroField';
import { FollowedHeroField } from '../followedHero/FollowedHeroField';
import { SignalField } from '@uifabric/experiments/lib/components/signals/SignalField';
import * as HeroStyles from '../dotdotdot/DotDotDotField.scss';
import { WithActionSet, IWithActionSetStateProps, evaluateAction, INITIAL_ACTION_STATE } from '../../withAction/WithAction';
import { ItemShareAction } from '../../actions/share/ShareAction';
import { ItemFollowAction } from '../../actions/follow/FollowAction';
import { IItemHoverCardProps } from '../../../controls/itemHoverCardControl';
import CommandStrings from '../../../components/commands/Commands.resx';
import { mobileOpenControl } from '../../../controls/mobileOpenControl';
import { getItemHoverCard } from '../../fileHoverCard/getItemHoverCard';
import { Fragment } from '../../../utilities/components/Fragment';

interface ISignalNameFieldProp {
  beforeSignals: React.ReactNode[] | React.ReactNode;
  afterSignals: React.ReactNode[] | React.ReactNode;
  signalTitles?: string[];
  hoverCard: React.ComponentType<IItemHoverCardProps> | undefined;
  shareAction?: JSX.Element | null;
  followAction?: JSX.Element | null;
  children?: React.ReactNode;
}

interface IHeroActions {
  share: JSX.Element | null;
  follow: JSX.Element | null;
}

const LINE_BREAK = '\u000D';

export const NameHeroField: React.ComponentType<{
  itemKey: string;
} & Pick<INameFieldProps, 'isDisabled' | 'onClick'>> = connectToItemCache((itemCache: IItemCache, props: {
  itemKey: string;
}): Pick<INameFieldProps, 'linkText' | 'mobileOpenProperty'> & ISignalNameFieldProp => {
  const {
    itemKey,
  } = props;

  const displayName = itemCache.demandItemFacet(displayNameFacet, itemKey);

  // Get signal icons
  const newSignalData = getNewSignal(itemCache, itemKey, '0'/*key*/);
  const beforeSignals = newSignalData ? [newSignalData.signal] : undefined;
  const afterSignalsData = getAfterSignals(itemCache, itemKey);
  const signalTitles = afterSignalsData.titles;
  let afterSignals: React.ReactNode[] | undefined = afterSignalsData.signals;
  if (afterSignals && afterSignals.length === 0) {
    afterSignals = undefined;
  }

  const mobileOpenControlHandler = itemCache.demandItemFacet(mobileOpenControl, itemKey);

  const {
    mobileOpenProperty
  } = mobileOpenControl.evaluate(mobileOpenControlHandler)(itemCache, {
    itemKey: itemKey
  });

  const shareAction = evaluateAction(itemCache, (
    <ItemShareAction itemKey={ itemKey } />
  ));

  const followAction = evaluateAction(itemCache, (
    <ItemFollowAction itemKey={ itemKey } />
  ));

  return {
    beforeSignals: beforeSignals,
    afterSignals: afterSignals,
    signalTitles: signalTitles,
    linkText: displayName,
    hoverCard: getItemHoverCard(itemCache, { itemKey }),
    mobileOpenProperty: mobileOpenProperty,
    shareAction,
    followAction
  };
})((props: INameFieldProps & ISignalNameFieldProp & {
  itemKey: string;
}) => {
  const {
    itemKey,
    beforeSignals,
    afterSignals,
    signalTitles,
    hoverCard: HoverCard,
    followAction,
    shareAction,
    ...nameFieldProps
  } = props;

  const onclickDot = (ev: React.MouseEvent<HTMLButtonElement>) => {
    let menuEvent = document.createEvent('CustomEvent');
    menuEvent.initCustomEvent('contextmenu', true /*canBubble*/, true /*cancelable*/, ev);
    ev.currentTarget.dispatchEvent(menuEvent);
  };

  const nameFieldTitle = (signalTitles &&
    (nameFieldProps.linkText + (nameFieldProps.linkText ? LINE_BREAK : '') + signalTitles.join(LINE_BREAK))) || '';

  const actions: IHeroActions = {
    share: shareAction as JSX.Element,
    follow: followAction as JSX.Element
  };

  return (
    <WithActionSet<keyof IHeroActions> actions={ actions } >
      { ({ actions: actionStates }: IWithActionSetStateProps<keyof IHeroActions>) => {
        const {
          share: {
            isAvailable: isShareAvailable,
            execute: executeShare
          } = INITIAL_ACTION_STATE,
          follow: {
            isAvailable: isFollowAvailable,
            isToggled: hasFollowed = false,
            execute: executeFollow
          } = INITIAL_ACTION_STATE
        } = actionStates;

        const onClickShare = isShareAvailable && executeShare ? (event: React.MouseEvent<HTMLButtonElement>) => {
          event.persist();
          executeShare({ event }).catch(() => {
            // Ignore errors.
          });
        } : undefined;

        const onClickFollow = isFollowAvailable && executeFollow ? (event: React.MouseEvent<HTMLButtonElement>) => {
          event.persist();
          executeFollow({ event }).catch(() => {
            // Ignore errors.
          });
        } : undefined;

        const dotdotdotBtnProps: IDotDotDotFieldProps = {
          onClick: onclickDot,
          ariaLabel: CommandStrings.ShowActions,
          title: CommandStrings.ShowActions
        };

        const content = (
          <div className={ `${HeroStyles.fieldRendererRendererWithCommands}` }>
            <div className={ `${HeroStyles.nameFieldWidthOnHover}` }>
              <SignalField
                signalsFieldMode='wide'
                before={ beforeSignals }
                after={ !!afterSignals &&
                  <span className={ `${HeroStyles.signalsShowHide}` }>
                    { afterSignals }
                  </span> }
              >
                <NameField
                  ariaDescription={ !!HoverCard ? CommandStrings.FileHoverCardShortcutHint : undefined }
                  isDisabled={ props.isDisabled }
                  linkText={ nameFieldProps.linkText }
                  onClick={ nameFieldProps.onClick }
                  mobileOpenProperty={ props.mobileOpenProperty }
                  title={ nameFieldTitle }
                  disableTooltip={ !!HoverCard }
                />
              </SignalField>
              { props.children || null }
            </div>
            <div className={ `${HeroStyles.fieldRendererHeroCommandContainer}` }>
              { onClickShare ? (
                <ShareHeroField
                  onClick={ onClickShare }
                  ariaLabel={ CommandStrings.ShareCommandTooltipV3 }
                  title={ CommandStrings.ShareCommandTooltipV3 }
                />) : null }
              { onClickFollow ? (<FollowedHeroField onClick={ onClickFollow } hasFollowed={ hasFollowed } />) : null }
              <DotDotDotField { ...dotdotdotBtnProps } />
            </div>
          </div >
        );

        return (
          <Fragment>
            { HoverCard ?
              <HoverCard itemKey={ itemKey }>
                { content }
              </HoverCard> :
              content
            }
          </Fragment>
        );
      } }
    </WithActionSet>
  );
});

function getAfterSignals(itemCache: IItemCache, itemKey: string): {
  signals: React.ReactNode[],
  titles: string[]
} {
  let storageSignal = getStorageSignal(itemCache, itemKey) || null;

  const maxLifeCycleCount = storageSignal ? 2 : 3;
  let signalsData = getLifeCycleSignals(itemCache, itemKey, maxLifeCycleCount, false/*includeNew*/) || [];
  if (storageSignal && (Array.isArray(signalsData))) {
    signalsData = signalsData.concat(storageSignal);
  }

  let signals: React.ReactNode[] = [];
  let titles: string[] = [];

  signalsData.forEach((signalData: ISignalData) => {
    signals.push(signalData.signal);
    titles.push(signalData.title || '');
  });

  return {
    signals: signals,
    titles: titles
  };
}
